import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Users, Circle, ChevronDown, ChevronUp, X } from 'lucide-react';
import { dedupedGet } from '../../lib/requestDedupe';
import { usePoller } from '../../hooks/usePoller';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  active:   { label: 'Active',   color: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50',  border: 'border-emerald-200', ring: 'ring-emerald-500/20' },
  idle:     { label: 'Idle',     color: 'bg-amber-400',   text: 'text-amber-700',   bg: 'bg-amber-50',    border: 'border-amber-200',   ring: 'ring-amber-400/20' },
  on_break: { label: 'Break',    color: 'bg-blue-500',    text: 'text-blue-700',    bg: 'bg-blue-50',     border: 'border-blue-200',    ring: 'ring-blue-500/20' },
  it_issue: { label: 'IT Issue', color: 'bg-red-500',     text: 'text-red-700',     bg: 'bg-red-50',      border: 'border-red-200',     ring: 'ring-red-500/20' },
  inactive: { label: 'Offline',  color: 'bg-gray-300',    text: 'text-gray-500',    bg: 'bg-gray-50',     border: 'border-gray-200',    ring: 'ring-gray-300/20' },
};

const ROLE_LABELS = {
  admin: 'Admin',
  order_desk: 'Order Desk',
  team_leader: 'Team Leader',
  tax_search_team_leader: 'Tax TL',
  title_search_team_leader: 'Title TL',
  processor: 'Processor',
  search_processor: 'Search Proc.',
  title_search_processor: 'Title Proc.',
  tax_processor: 'Tax Proc.',
  qc_specialist: 'QC',
  vendor_manager: 'Vendor Mgr',
  accounts: 'Accounts',
  jurisdiction_manager: 'Jurisdiction Mgr',
};

const formatMinutes = (mins) => {
  if (mins == null) return '--';
  const m = Math.round(mins);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm > 0 ? `${h}h ${rm}m ago` : `${h}h ago`;
};

const SummaryPill = ({ label, count, status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.inactive;
  return (
    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full ${cfg.bg} ${cfg.border} border`} data-testid={`team-summary-${status}`}>
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${cfg.color}`} />
      <span className={`text-xs font-semibold ${cfg.text}`}>{count}</span>
      <span className={`text-[10px] ${cfg.text} opacity-70`}>{label}</span>
    </div>
  );
};

const MemberRow = ({ member }) => {
  const cfg = STATUS_CONFIG[member.status] || STATUS_CONFIG.inactive;
  const roleLabel = ROLE_LABELS[member.role] || member.role || '--';

  return (
    <div className="flex items-center gap-2.5 py-2 px-3 hover:bg-gray-50/80 transition-colors rounded-lg" data-testid={`team-member-${member.user_id}`}>
      <div className="relative flex-shrink-0">
        <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center">
          <span className="text-[10px] font-semibold text-slate-600 uppercase">
            {(member.user_name || '?').split(' ').map(w => w[0]).join('').slice(0, 2)}
          </span>
        </div>
        <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ${cfg.color} border-2 border-white`} />
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-800 truncate leading-tight">{member.user_name}</p>
        <p className="text-[10px] text-gray-400 truncate leading-tight">{roleLabel}</p>
      </div>

      <div className="flex-shrink-0 text-right">
        <span className={`text-[10px] font-medium ${cfg.text}`}>{cfg.label}</span>
        <p className="text-[10px] text-gray-400 leading-tight">{formatMinutes(member.minutes_since_activity)}</p>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Drawer rendered via React Portal so it escapes the header DOM      */
/* ------------------------------------------------------------------ */
const DrawerPortal = ({ open, onClose, data, loading, error, showInactive, setShowInactive }) => {
  const drawerRef = useRef(null);
  const summary = data?.summary || {};
  const members = data?.team_members || [];
  const activeMembers = members.filter(m => m.status !== 'inactive');
  const inactiveMembers = members.filter(m => m.status === 'inactive');

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  // Close on click outside drawer
  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        const trigger = document.querySelector('[data-testid="team-activity-trigger"]');
        if (trigger && trigger.contains(e.target)) return;
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 bg-black/20 z-[60] transition-opacity"
          data-testid="team-activity-backdrop"
          onClick={onClose}
        />
      )}

      {/* Drawer — full-height, right-anchored */}
      <div
        ref={drawerRef}
        data-testid="team-activity-panel"
        style={{ height: '100vh' }}
        className={`fixed top-0 right-0 w-[350px] bg-white shadow-2xl border-l border-gray-200 z-[70] flex flex-col transform transition-transform duration-300 ease-in-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-slate-50 to-white flex-shrink-0">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-semibold text-slate-700">Team Activity</span>
            {summary.total != null && (
              <span className="text-[10px] font-medium text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{summary.total}</span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
            data-testid="team-activity-close"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* Summary pills */}
        <div className="px-5 py-3 flex flex-wrap gap-1.5 border-b border-gray-50 flex-shrink-0">
          <SummaryPill label="Active" count={summary.active || 0} status="active" />
          <SummaryPill label="Idle" count={summary.idle || 0} status="idle" />
          <SummaryPill label="Break" count={summary.on_break || 0} status="on_break" />
          <SummaryPill label="IT" count={summary.it_issue || 0} status="it_issue" />
        </div>

        {/* Scrollable member list — fills remaining height */}
        <div className="flex-1 overflow-y-auto px-2 py-1">
          {/* TEAM-SCOPE-004: SSOT mapping gap notice — the TL's own session may
              still render below, so this banner shows whenever scope is empty */}
          {!loading && data?.scope_member_count === 0 && (
            <div className="mx-2 mt-2 mb-1 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200" data-testid="team-activity-no-mapping">
              <p className="text-[11px] font-semibold text-amber-800">No team members are currently mapped to this team.</p>
              <p className="text-[10.5px] text-amber-600 mt-0.5">Ask your administrator to assign members to your team.</p>
            </div>
          )}
          {loading && !data && (
            <div className="flex items-center justify-center py-16">
              <div className="w-5 h-5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
            </div>
          )}

          {error && !data && (
            <p className="text-xs text-red-500 text-center py-8">{error}</p>
          )}

          {!loading && activeMembers.length === 0 && inactiveMembers.length === 0 && data?.scope_member_count !== 0 && (
            <p className="text-xs text-gray-400 text-center py-8" data-testid="team-activity-empty">No team members clocked in</p>
          )}

          {activeMembers.map(m => (
            <MemberRow key={m.user_id} member={m} />
          ))}

          {inactiveMembers.length > 0 && (
            <>
              <button
                onClick={() => setShowInactive(v => !v)}
                className="w-full flex items-center gap-1.5 px-3 py-1.5 mt-1 text-[10px] text-gray-400 hover:text-gray-600 transition-colors"
                data-testid="show-inactive-toggle"
              >
                <Circle className="w-2 h-2" />
                <span>{showInactive ? 'Hide' : 'Show'} {inactiveMembers.length} offline</span>
                {showInactive ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />}
              </button>
              {showInactive && inactiveMembers.map(m => (
                <MemberRow key={m.user_id} member={m} />
              ))}
            </>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};

/* ------------------------------------------------------------------ */
/*  Main export — trigger button + portal drawer                       */
/* ------------------------------------------------------------------ */
const TeamActivityPanel = ({ currentUser }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  // TEAM-SCOPE-004 (Aug 2026): aligned with the FULL Team Leader role list the
  // backend already authorizes (legacy spellings included). Backend scoping is
  // unchanged — every TL still receives ONLY their own team.
  const isAllowed = ['admin', 'team_leader', 'tax_team_leader', 'title_team_leader', 'manager',
    'tax_search_team_leader', 'title_search_team_leader'].includes(currentUser?.role);

  const fetchData = useCallback(async (signal) => {
    if (!isAllowed) return;
    try {
      const res = await dedupedGet(`${API}/api/session-health/team-status`, { signal });
      setData(res.data);
      setError(null);
    } catch (err) {
      if (err.code === 'ERR_CANCELED' || err.name === 'CanceledError') {
        console.warn('[TeamActivity] poll exceeded its 25s window and was cancelled — retrying with backoff, keeping last data');
        return false;
      }
      console.error('[TeamActivity] Fetch failed:', err.message);
      setError('Unable to load');
      return false;
    } finally {
      setLoading(false);
    }
  }, [isAllowed]);

  useEffect(() => {
    if (!isAllowed) return;
    fetchData();
    // PRESENCE-FRESHNESS (Feb 2026):
    //   • 30s poll + visible-tab refetch — handled by usePoller below.
    //   • storage — when ANOTHER tab clears its `token` (i.e. the user
    //     logged out elsewhere), every open dashboard refetches
    //     immediately. Cross-tab sync without a websocket.
    const handleStorage = (e) => {
      // Only react to auth-related storage changes from other tabs.
      if (e.key === 'token' || e.key === null) fetchData();
    };
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [fetchData, isAllowed]);

  // POLL-HERD-001: staggered, overlap-safe, backoff-aware polling.
  usePoller(fetchData, 30000, { enabled: isAllowed, immediate: false });

  const handleClose = useCallback(() => setOpen(false), []);

  if (!isAllowed) return null;

  const activeCount = data?.summary?.active || 0;

  return (
    <>
      {/* Trigger — lives in header */}
      <button
        data-testid="team-activity-trigger"
        onClick={() => setOpen(prev => !prev)}
        className={`relative flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
          open
            ? 'bg-blue-50 text-blue-700 border border-blue-200'
            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
        }`}
        title="Live Team Activity"
      >
        <Users className="h-4 w-4" />
        <span className="hidden sm:inline">Activity</span>
        {activeCount > 0 && (
          <span className="inline-flex items-center justify-center w-4 h-4 text-[9px] font-bold text-white bg-emerald-500 rounded-full">
            {activeCount}
          </span>
        )}
      </button>

      {/* Drawer — portalled to <body> */}
      <DrawerPortal
        open={open}
        onClose={handleClose}
        data={data}
        loading={loading}
        error={error}
        showInactive={showInactive}
        setShowInactive={setShowInactive}
      />
    </>
  );
};

export default TeamActivityPanel;
